/**
 * `<HiredLedgerGrid>` — the ≥900px Hired Candidates ledger (Concept A).
 *
 * Every hired candidate is a row; every analytics variable is an editable cell.
 *
 * ── The one navigating element ───────────────────────────────────────────────
 * The candidate's NAME is a link to their candidate page. Nothing else
 * navigates, and the ROW deliberately does not: on a grid whose whole purpose is
 * filling cells, a row-level click target means every mistimed click throws you
 * off the page and loses the edit. So no `onRowClick` here.
 *
 * The `/outcomes/:candidateId` deep-dive is still reachable — as a small
 * secondary "↗" affordance at the end of the row, not as the row's primary
 * action.
 *
 * ── Layout ───────────────────────────────────────────────────────────────────
 * The candidate column is pinned (`sticky left-0`) and the rest scrolls
 * horizontally INSIDE the table frame — `DataTableV2`'s wrapper already owns
 * `overflow-x-auto`, so the page body never scrolls sideways.
 *
 * Reuses `DataTableV2` for chrome, density and sort affordances rather than
 * hand-rolling a table, so the ledger reads as the same furniture as All
 * Candidates and the Actions Log.
 */
import type { JSX } from "react";
import { Link } from "react-router-dom";
import { DataTableV2, type DataTableColumn } from "@/components/v2/patterns/DataTableV2";
import { CandidatePhoto } from "@/components/candidates/CandidatePhoto";
import { cn } from "@/lib/utils";
import type {
  HiredLedgerItem,
  HiredLedgerSort,
  SortDirection,
} from "@/hooks/queries/outcomes/useHiredLedger";
import { LedgerCell } from "./LedgerCell";
import { CompletenessMeter } from "./CompletenessMeter";
import {
  fieldsForGroup,
  type LedgerColumnGroup,
  type LedgerField,
  type LedgerFieldKey,
} from "./ledgerFields";

// Pinned first column. `bg-surface-card` is required, not decorative — without
// an opaque background the scrolling cells show through the pinned one.
const PIN_CLS = "sticky left-0 z-[2] bg-surface-card";

interface HiredLedgerGridProps {
  rows: HiredLedgerItem[];
  group: LedgerColumnGroup;
  sort: HiredLedgerSort;
  direction: SortDirection;
  onSort: (field: string) => void;
  onCellCommit: (
    row: HiredLedgerItem,
    field: LedgerFieldKey,
    value: string | boolean | null,
  ) => void;
  /** `${candidate_id}:${field}` → message, for cells whose last write failed. */
  cellErrors: Record<string, string>;
  /** Base tenant path, e.g. `/acme`. */
  base: string;
  /** False for a hiring manager — outcomes are read-only for them (backend 403s). */
  canEdit: boolean;
  emptyState?: JSX.Element;
}

export function HiredLedgerGrid({
  rows,
  group,
  sort,
  direction,
  onSort,
  onCellCommit,
  cellErrors,
  base,
  canEdit,
  emptyState,
}: HiredLedgerGridProps): JSX.Element {
  const fields = fieldsForGroup(group);

  const columns: DataTableColumn[] = [
    {
      key: "candidate",
      header: "Candidate",
      width: "232px",
      sortField: "candidate",
      headClassName: PIN_CLS,
      cellClassName: PIN_CLS,
    },
    {
      key: "completeness",
      header: "Recorded",
      width: "112px",
      sortField: "completeness",
    },
    ...fields.map(
      (f): DataTableColumn => ({
        key: f.key,
        header: f.label,
        width: f.width,
        sortField: f.sortKey,
      }),
    ),
    { key: "call", header: "Our call", width: "150px" },
    { key: "detail", header: "", width: "48px", cellClassName: "text-right" },
  ];

  const renderCell = (row: HiredLedgerItem, col: DataTableColumn): JSX.Element => {
    switch (col.key) {
      case "candidate":
        return (
          <div className="flex items-center gap-[10px]">
            <CandidatePhoto
              candidateId={row.candidate_id}
              candidateName={row.candidate_name}
              size="sm"
            />
            <div className="min-w-0">
              {/* The ONLY navigating element in the row. */}
              <Link
                to={`${base}/candidates/${row.candidate_id}`}
                data-testid={`ledger-name-${row.candidate_id}`}
                className={cn(
                  "block truncate text-text-primary hover:underline",
                  "rounded-sm focus-visible:outline-none focus-visible:ring-2",
                  "focus-visible:ring-border-focus",
                )}
                style={{ fontWeight: 600 }}
              >
                {row.candidate_name}
              </Link>
              <div className="truncate text-text-secondary text-[12px]">
                {row.role ?? "Role not recorded"}
              </div>
            </div>
          </div>
        );

      case "completeness":
        return (
          <CompletenessMeter
            filled={row.completeness}
            total={row.completeness_total}
          />
        );

      case "call":
        return <OurCallCell row={row} />;

      case "detail":
        return (
          <Link
            to={`${base}/outcomes/${row.candidate_id}${
              row.jd_id ? `?jd=${row.jd_id}` : ""
            }`}
            aria-label={`Open the outcome detail for ${row.candidate_name}`}
            title="Outcome detail"
            data-testid={`ledger-detail-${row.candidate_id}`}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius)]",
              "text-text-tertiary transition-colors hover:bg-surface-subtle hover:text-text-primary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
            )}
          >
            <span aria-hidden="true" className="text-[12px]">
              ↗
            </span>
          </Link>
        );

      default: {
        const field = fields.find((f) => f.key === col.key) as LedgerField | undefined;
        if (!field) return <span />;
        const key = `${row.candidate_id}:${field.key}`;
        return (
          <LedgerCell
            field={field}
            value={row[field.key]}
            candidateName={row.candidate_name}
            disabled={!canEdit}
            errorMessage={cellErrors[key] ?? null}
            testId={`ledger-cell-${field.key}-${row.candidate_id}`}
            onCommit={(value) => onCellCommit(row, field.key, value)}
          />
        );
      }
    }
  };

  return (
    <DataTableV2
      columns={columns}
      rows={rows}
      getRowKey={(row) => row.candidate_id}
      renderCell={renderCell}
      sortField={sort}
      sortDir={direction}
      onSort={onSort}
      emptyState={emptyState}
      className="[&_td]:py-[7px]"
    />
  );
}

// ── "Our call" — read-only prediction vs. realized ────────────────────────────
// Derived server-side from the terminal variables; not editable, and rendered as
// muted text rather than a chip when there is nothing to grade. No fabricated
// confidence where the data does not support one.

type FitBand = "strong" | "moderate" | "weak";

/** Backend thresholds (app/services/fit_bands.py): strong ≥80, moderate 50–79. */
function fitBand(score: number | null): FitBand | null {
  if (score == null) return null;
  if (score >= 80) return "strong";
  if (score >= 50) return "moderate";
  return "weak";
}

const BAND_LABEL: Record<FitBand, string> = {
  strong: "Strong",
  moderate: "Moderate",
  weak: "Weak",
};

const VERDICT_LABEL = {
  confirmed: "Confirmed",
  over_called: "Over-called",
  under_called: "Under-called",
  pending: "Pending",
} as const;

function OurCallCell({ row }: { row: HiredLedgerItem }): JSX.Element {
  const band = fitBand(row.predicted_fit_score);
  if (band == null) {
    return <span className="text-text-tertiary text-[12.5px]">Not scored</span>;
  }
  const fg =
    band === "strong"
      ? "hsl(var(--strong))"
      : band === "moderate"
        ? "hsl(var(--moderate))"
        : "hsl(var(--weak))";
  const bg =
    band === "strong"
      ? "hsl(var(--strong-soft))"
      : band === "moderate"
        ? "hsl(var(--moderate-soft))"
        : "hsl(var(--weak-soft))";
  return (
    <span className="inline-flex flex-col gap-[2px]">
      <span
        className="inline-flex w-fit items-center gap-[5px] tabular-nums"
        style={{
          borderRadius: "999px",
          padding: "1px 8px",
          fontSize: "11px",
          fontWeight: 600,
          background: bg,
          color: fg,
        }}
      >
        {row.predicted_fit_score} · {BAND_LABEL[band]}
      </span>
      <span className="text-text-tertiary text-[11px]">
        {row.verdict ? VERDICT_LABEL[row.verdict] : "Pending"}
      </span>
    </span>
  );
}
